import { z } from 'zod';

import { defineFcm } from '../shared/factory';

// A bare hostname, e.g. "bit.ly". An entry with a scheme, path, port, whitespace or leading dot would
// never match the hostname of a URL found in a comment, so reject it instead of silently ignoring it.
const hostEntry = z
	.string()
	.regex(
		/^[a-z0-9-]+(\.[a-z0-9-]+)*\.?$/i,
		'Must be a bare hostname such as "bit.ly": no scheme, path, port, spaces or leading dot',
	);

export const FCM_COMMENT_BLOCKED_URL_HOSTS = defineFcm('comment_blocked_url_hosts')
	.asJson(
		z.object({
			domains: z.array(hostEntry),
			exactHosts: z.array(hostEntry),
			replacement: z.record(z.string(), z.string()).and(z.object({ 'en-US': z.string() })),
		}),
	)
	.withDefault({ domains: [], exactHosts: [], replacement: { 'en-US': '[REDACTED]' } })
	.toConfig({
		owner: 'squad-site-platform',
		scope: 'corgi-middle-end',
		supportedGroups: { global: true },
		context: 'Supporter comments and reasons for signing',
		description: `Hosts whose URLs are blocked in public comment text: supporter comments, reasons for signing, and the legacy comment types.

			What it does:
			- Any URL on a blocked host is replaced with "replacement" in the public "text" and "textStripped" fields, including for existing comments.
			- A new comment or signature whose text contains a blocked URL is rejected on submit.

			Matching:
			- Entries must be bare hostnames (e.g. "bit.ly"). A value with a scheme, path, port, spaces or a leading dot is rejected.
			- "domains" entries (e.g. "bit.ly") match that domain and all its subdomains.
			- "exactHosts" entries (e.g. "www.bit.ly") match only that exact host, not its subdomains and not its parent domain.
			- Matching is case-insensitive, and scheme-less URLs such as "bit.ly/abc" are detected.
			- Obfuscated URLs (e.g. "bit . ly") are not detected.

			Replacement:
			- "replacement" is keyed by locale. The viewer's locale is used, falling back to "en-US".
			- When the response isn't tied to a session (so it's cacheable), "en-US" is always used.
			- Keep it plain text with no URL or domain in it: the replacement is inserted before autolinking and redirect wrapping, so anything URL-like would be turned into a link.

			"textRaw" is never redacted and stays publicly readable.`,
		template: {
			domains: ['bit.ly', 'bitly.com', 'changevoices.org', 'petitionpromotions.com'],
			exactHosts: [],
			replacement: { 'en-US': '[REDACTED]' },
		},
	});
